import React, { useState, useCallback, useEffect, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../common/button/Button';
import { validateSlug, generateSlug, SLUG_MIN_LENGTH } from '../../utils/slugUtils';
import { createAgent, checkSlugAvailability } from '../../api/agents/agents';

import styles from './styles/CreateAgentForm.module.css';

const initialFormState = {
    name: '',
    slug: '',
};

const CreateAgentForm = ({ onClose, onCreated = () => {} }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialFormState);
    const [loading, setLoading] = useState(false);
    const [checkingSlug, setCheckingSlug] = useState(false);
    const [error, setError] = useState(null);
    const [slugError, setSlugError] = useState(null);
    const [suggestedSlug, setSuggestedSlug] = useState('');

    // Reset all form state
    const resetForm = useCallback(() => {
        setFormData(initialFormState);
        setError(null);
        setSlugError(null);
        setSuggestedSlug('');
        setCheckingSlug(false);
    }, []);

    // Handle form close with reset
    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [onClose, resetForm]);

    // Check slug availability with debounce
    useEffect(() => {
        if (!formData.slug) {
            setSlugError(null);
            setCheckingSlug(false);
            return;
        }

        const validation = validateSlug(formData.slug);
        if (!validation.isValid) {
            setSlugError(validation.error);
            setCheckingSlug(false);
            return;
        }

        const debounceTimer = setTimeout(async () => {
            setCheckingSlug(true);
            try {
                const result = await checkSlugAvailability(formData.slug);
                if (!result.available) {
                    setSlugError(result.error || 'This slug is already taken');
                } else {
                    // Clear error when slug becomes available
                    setSlugError(null);
                }
            } catch (err) {
                console.error('Error checking slug:', err);
                setSlugError('Error checking slug availability');
            } finally {
                setCheckingSlug(false);
            }
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [formData.slug]);

    // Apply suggested slug to form
    const applySlugSuggestion = useCallback(() => {
        if (suggestedSlug) {
            const validation = validateSlug(suggestedSlug);
            if (!validation.isValid) {
                setSlugError(validation.error);
                // Apply slug anyway so user can fix it
                setFormData(prev => ({ ...prev, slug: suggestedSlug }));
            } else {
                // Slug is valid, apply it and clear errors
                setFormData(prev => ({ ...prev, slug: suggestedSlug }));
                setSlugError(null);
            }
        }
    }, [suggestedSlug]);

    // Handle input changes
    const handleChange = useCallback(e => {
        const { name, value } = e.target;
        let newValue = name === 'slug' ? value.toLowerCase().replace(/ /g, '_') : value;

        setFormData(prev => ({ ...prev, [name]: newValue }));

        // Reset errors when fields change
        setError(null);

        if (name === 'slug') {
            // Client-side slug validation
            if (newValue) {
                const validation = validateSlug(newValue);
                setSlugError(validation.isValid ? null : validation.error);
            } else {
                setSlugError(null);
            }
        }

        // Generate slug suggestion when name changes
        if (name === 'name') {
            const newSlug = generateSlug(value);
            setSuggestedSlug(newSlug);
        }
    }, []);

    // Handle form submission
    const handleSubmit = useCallback(async e => {
        e.preventDefault();

        // Validation
        if (!formData.name || !formData.slug) {
            setError('Please fill in all required fields');
            return;
        }

        const slugValidation = validateSlug(formData.slug);
        if (!slugValidation.isValid) {
            setError(slugValidation.error);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Create agent via API
            const agent = await createAgent(formData);

            // Navigate to agent page
            navigate(`/agents/${agent.slug}/settings`);

            // Notify parent about creation and close the form
            try { onCreated(agent); } catch (_) {}
            onClose();
        } catch (err) {
            console.error('Error creating agent:', err);
            setError(err.message || 'Failed to create agent. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [formData, navigate, onClose, onCreated]);

    return (
        <div className={styles.container}>
            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.inputGroup}>
                    <label htmlFor="name" className={styles.label}>
                        Agent Name *
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter agent name"
                        className={styles.input}
                    />
                </div>

                <div className={styles.inputGroup}>
                    <label htmlFor="slug" className={styles.label}>
                        Agent Slug *
                    </label>
                    <input
                        type="text"
                        id="slug"
                        name="slug"
                        value={formData.slug}
                        onChange={handleChange}
                        required
                        placeholder="unique_agent_identifier"
                        className={`${styles.input} ${slugError ? styles.inputError : ''}`}
                    />
                    {slugError && (
                        <small className={styles.errorText}>
                            {slugError}
                        </small>
                    )}
                    {checkingSlug && (
                        <small className={styles.helpText}>
                            Checking availability...
                        </small>
                    )}
                    <small className={styles.helpText}>
                        Only lowercase letters, numbers, and underscores. Must contain at least one letter. Minimum {SLUG_MIN_LENGTH} characters.
                    </small>
                    {suggestedSlug && suggestedSlug !== formData.slug && (
                        <small
                            className={styles.suggestion}
                            onClick={applySlugSuggestion}
                        >
                            Suggestion: {suggestedSlug} (click to use)
                        </small>
                    )}
                    <small className={styles.noteText}>
                        <strong>Note:</strong> Slug cannot be changed after creation (only through support).
                    </small>
                    <small className={styles.helpText}>
                        All detailed settings will be available on the agent page after creation.
                    </small>
                </div>

                {error && (
                    <div className={styles.errorMessage}>
                        {error}
                    </div>
                )}

                <div className={styles.actions}>
                    <Button
                        variant="secondary"
                        size="small"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        size="small"
                        disabled={loading || slugError || checkingSlug}
                    >
                        {loading ? 'Creating...' : 'Create Agent'}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default memo(CreateAgentForm);
